import NextNprogress from 'nextjs-progressbar';
import { AppetizerProvider } from '@getinapp/appetizer';
import { ReactQueryDevtools } from 'react-query/devtools';
import { QueryClientProvider } from 'react-query';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import GlobalStyles from 'styles/global';
import theme from 'styles/theme';
import { queryClient } from 'configs/react-query';
import i18n from 'configs/i18n';
import { AuthProvider } from 'hooks/use-auth';
import { LineProvider } from 'hooks/use-line';
import { SearchUnitsContextProvider } from 'hooks/use-search-units';
import { ReservationsResultsProvider } from 'context/ReservationsResultsContext';
import { OccasionProvider } from 'hooks/use-occasions';
import { ToastContainer } from 'react-toastify';
import { STATIC_RESOURCES_BASE_URL } from 'utils/constants';
import 'react-toastify/dist/ReactToastify.css';

function App({ Component, pageProps }: AppProps) {
  const { t } = useTranslation();
  return (
    <AppetizerProvider>
      <I18nextProvider i18n={i18n}>
        <Script
          async
          id="gtm"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];
            function gtag() { dataLayer.push(arguments);}
            gtag('set', 'cookie_flags', 'secure;samesite=none');(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-5WW4HJM3');`,
          }}
        />
        <Script
          async
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=GTM-5WW4HJM3`}
        />
        <Head>
          <noscript>
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=GTM-5WW4HJM3"
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            ></iframe>
          </noscript>
          <title>{t('@Head.title')}</title>
          <meta name="description" content={t('@Head.description')} />
          <meta name="theme-color" content={theme.colors.primary} />
          <link
            rel="shortcut icon"
            href={`${STATIC_RESOURCES_BASE_URL}/b2c/images/icon-192-rounded.webp`}
          />
          <link
            rel="apple-touch-icon"
            href={`${STATIC_RESOURCES_BASE_URL}/b2c/images/icon-512.webp`}
          />
          <link rel="manifest" href="/manifest.json" />
          <link
            rel="preload"
            href={`${STATIC_RESOURCES_BASE_URL}/fonts/MaisonNeueExt-Light.ttf`}
            as="font"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href={`${STATIC_RESOURCES_BASE_URL}/fonts/ACaslonPro-Italic.otf`}
            as="font"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href={`${STATIC_RESOURCES_BASE_URL}/fonts/MaisonNeueExt-Medium.ttf`}
            as="font"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href={`${STATIC_RESOURCES_BASE_URL}/fonts/MaisonNeueExt-Bold.ttf`}
            as="font"
            crossOrigin="anonymous"
          />
        </Head>

        <Script
          id="heap-analytics"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (window.heapReadyCb = window.heapReadyCb || []),
              (window.heap = window.heap || []),
              (heap.load = function(e, t) {
                (window.heap.envId = e),
                (window.heap.clientConfig = t = t || {}),
                (window.heap.clientConfig.shouldFetchServerConfig = !1);
                var a = document.createElement('script');
                (a.type = 'text/javascript'),
                (a.async = !0),
                (a.src = 'https://cdn.us.heap-api.com/config/' + e + '/heap_config.js');
                var r = document.getElementsByTagName('script')[0];
                r.parentNode.insertBefore(a, r);
                var n = ['init','startTracking','stopTracking','track','resetIdentity','identify','identifyHashed','getSessionId','getUserId','getIdentity','addUserProperties','addEventProperties','removeEventProperty','clearEventProperties','addAccountProperties','addAdapter','addTransformer','addTransformerFn','onReady','addPageviewProperties','removePageviewProperty','clearPageviewProperties','trackPageview'],
                i = function(e) {
                  return function() {
                    var t = Array.prototype.slice.call(arguments, 0);
                    window.heapReadyCb.push({ name: e, fn: function() { heap[e] && heap[e].apply(heap, t); } });
                  };
                };
                for (var p = 0; p < n.length; p++) heap[n[p]] = i(n[p]);
              });
              heap.load('${process.env.NEXT_PUBLIC_HEAP_ID}');
            `,
          }}
        />

        <Script
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY}&v=3.exp&libraries=geometry,drawing,places`}
          defer
          strategy="afterInteractive"
        />

        <NextNprogress
          color={theme.colors.primary}
          startPosition={0.3}
          options={{ showSpinner: false }}
          stopDelayMs={200}
          height={3}
        />

        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LineProvider>
              <OccasionProvider>
                <SearchUnitsContextProvider>
                  <ThemeProvider theme={theme}>
                    <GlobalStyles />
                    <ToastContainer />
                    <ReservationsResultsProvider>
                      <Component {...pageProps} />
                    </ReservationsResultsProvider>
                  </ThemeProvider>
                </SearchUnitsContextProvider>
              </OccasionProvider>
            </LineProvider>
          </AuthProvider>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </I18nextProvider>
      <div hidden className="getin-widget-button" />
    </AppetizerProvider>
  );
}

export default App;
